<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  activeVisit: { type: Object, default: () => ({}) },
  currentDateText: { type: String, default: '—' },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'submit']);

const LABELS = Object.freeze({
  title: 'Reagendar visita',
  description:
    'Escolha o novo horário da visita. A alteração fica registrada no histórico e as automações de reagendamento são disparadas.',
  currentTime: 'Horário atual:',
  newDateTime: 'Nova data e hora *',
  reason: 'Motivo do reagendamento',
  reasonPlaceholder: 'Ex: cliente pediu antecipação',
  reasonHelper: 'Esse motivo ficará registrado no histórico da visita.',
  back: 'Voltar',
  submit: 'Reagendar visita',
});
const errorMessage = ref('');
const scheduledAt = ref('');
const reason = ref('');

const visitTitle = computed(
  () =>
    props.activeVisit?.imovel?.titulo ||
    props.activeVisit?.property?.title ||
    'Imóvel da visita'
);
const visitorName = computed(
  () => props.activeVisit?.contact?.name || 'Cliente não informado'
);

const pad = value => String(value).padStart(2, '0');

const toDateTimeLocal = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const resetForm = () => {
  scheduledAt.value = toDateTimeLocal(
    props.activeVisit?.scheduledAt || props.activeVisit?.scheduled_at
  );
  reason.value =
    props.activeVisit?.additionalAttributes?.visitReschedule?.reason ||
    props.activeVisit?.additional_attributes?.visit_reschedule?.reason ||
    '';
  errorMessage.value = '';
};

const submit = () => {
  errorMessage.value = '';

  if (!scheduledAt.value) {
    errorMessage.value = 'Escolha a nova data e horário da visita.';
    return;
  }

  const selectedDate = new Date(scheduledAt.value);
  if (Number.isNaN(selectedDate.getTime())) {
    errorMessage.value = 'Horário inválido.';
    return;
  }

  if (selectedDate.getTime() <= Date.now()) {
    errorMessage.value = 'Escolha um horário futuro para reagendar.';
    return;
  }

  if (!reason.value.trim()) {
    errorMessage.value = 'Informe o motivo do reagendamento.';
    return;
  }

  emit('submit', {
    scheduled_at: selectedDate.toISOString(),
    reason: reason.value.trim(),
  });
};

watch(
  () => props.show,
  isOpen => {
    if (isOpen) resetForm();
  }
);

watch(
  () => props.activeVisit?.id,
  () => {
    if (props.show) resetForm();
  }
);
</script>

<template>
  <LaisModal
    :show="show"
    :title="LABELS.title"
    max-width-class="max-w-[560px]"
    @close="emit('close')"
  >
    <div class="grid gap-4 text-[14px] leading-relaxed text-lais-text-body">
      <p>{{ LABELS.description }}</p>

      <section
        class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3"
      >
        <p class="font-semibold text-lais-text">{{ visitorName }}</p>
        <p class="mt-1 text-[13px] text-lais-text-muted">{{ visitTitle }}</p>
        <p class="mt-2 text-[13px] text-lais-text-muted">
          {{ LABELS.currentTime }} {{ currentDateText }}
        </p>
      </section>

      <label class="grid gap-1">
        <span class="font-semibold text-lais-text">
          {{ LABELS.newDateTime }}
        </span>
        <input
          v-model="scheduledAt"
          type="datetime-local"
          class="h-11 rounded-lais-sm border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none transition focus:border-lais-primary focus:shadow-lais-focus"
        />
      </label>

      <label class="grid gap-1">
        <span class="font-semibold text-lais-text">
          {{ LABELS.reason }}
        </span>
        <textarea
          v-model="reason"
          rows="3"
          class="rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text outline-none transition focus:border-lais-primary focus:shadow-lais-focus"
          :placeholder="LABELS.reasonPlaceholder"
        />
        <span class="text-[12px] text-lais-text-muted">
          {{ LABELS.reasonHelper }}
        </span>
      </label>

      <p
        v-if="errorMessage"
        class="rounded-lais-sm border border-lais-danger/30 bg-lais-danger/10 px-3 py-2 text-[13px] font-semibold text-lais-danger"
      >
        {{ errorMessage }}
      </p>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton
          variant="ghost"
          :disabled="loading"
          @click="emit('close')"
        >
          {{ LABELS.back }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-calendar-clock"
          :is-loading="loading"
          @click="submit"
        >
          {{ LABELS.submit }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
